import { goTo, replaceWith } from '../utils/pageNavigation';
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { authAPI } from '../utils/api';
import { trackSignupConversion, getReferralDataForConversion } from '../utils/affiliateTracking';
import { getAcquisition } from '../utils/acquisition';
import { getAdContext, trackSiteConversion } from '../utils/pixels';
import { identifyUser, trackEvent, resetUser } from '../utils/posthog';
import api from '../utils/api';
import { toUserFacingError } from '../utils/apiErrorHandler';
import { isAdminRole, planAccessFor } from '../utils/planAccess';
import { sessionEvents } from '../utils/sessionEvents';
import { noteSessionEnded } from '../utils/sessionEndedNotice';
import { refreshMeansSessionEnded } from '../utils/refreshFailure';
import { readAccessToken } from '../utils/accessToken';

// User roles
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  HOST: 'host',
  ATTENDEE: 'attendee'
};

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'canira_access_token',
  REFRESH_TOKEN: 'canira_refresh_token',
  USER: 'canira_user',
};

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get tokens from storage. The refresh token is intentionally NOT read from
  // localStorage — it lives only in the httpOnly `canira_refresh_token` cookie
  // (same-site with the API), out of reach of XSS. Extra callers that still
  // pass a refresh token to storeTokens are harmlessly ignored.
  // Shape-checked, so a poisoned "undefined" reads as "no token" rather than
  // being handed to a caller that would send it (see readAccessToken).
  const getTokens = useCallback(() => ({
    accessToken: readAccessToken(),
  }), []);

  // Store the access token only. The refresh token is never persisted to
  // JS-readable storage; the backend sets/rotates it as an httpOnly cookie.
  const storeTokens = useCallback((accessToken) => {
    if (accessToken) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    }
  }, []);

  // Clear tokens and user data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setCurrentUser(null);
  }, []);

  // Store user data
  const storeUser = useCallback((user) => {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    setCurrentUser(user);
  }, []);

  // The API client announces a dead session rather than navigating to /login
  // itself — it has no idea whether the current page needs auth, and forcing a
  // login on a PUBLIC page is how an emailed registration link became a login
  // wall for anyone with a stale token. Clearing the user here is what makes
  // PrivateRoute redirect a protected route (carrying `from`) while a public
  // page simply carries on signed out.
  useEffect(() => sessionEvents.subscribe((reason) => {
    // Carry WHY across the redirect. PrivateRoute cannot tell a host whose
    // session just ended from a visitor who was never signed in, so without
    // this both get the same unexplained login page. See
    // utils/sessionEndedNotice.
    noteSessionEnded(reason);
    resetUser();       // posthog identity
    clearAuthData();   // tokens + currentUser
  }), [clearAuthData]);

  // Initialize auth state from storage
  useEffect(() => {
    const initAuth = async () => {
      try {
        // One-time cleanup: purge any refresh token left in localStorage by a
        // previous app version. Refresh now runs off the httpOnly cookie, so a
        // stored copy is only an XSS liability. The user's session is unaffected
        // (access token + refresh cookie remain).
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const { accessToken } = getTokens();

        if (storedUser && accessToken) {
          // Verify token is still valid by fetching user profile
          try {
            const response = await authAPI.getProfile();
            if (response.data.success) {
              storeUser(response.data.user);
            } else {
              clearAuthData();
            }
          } catch (err) {
            // Token might be expired, try to refresh
            if (err.response?.status === 401) {
              const refreshed = await refreshToken();
              if (refreshed) {
                // Re-fetch profile with the new token
                try {
                  const retryResponse = await authAPI.getProfile();
                  if (retryResponse.data.success) {
                    storeUser(retryResponse.data.user);
                  } else {
                    clearAuthData();
                  }
                } catch {
                  // Refresh succeeded but profile still fails — use cached data
                  setCurrentUser(JSON.parse(storedUser));
                }
              } else if (refreshed === null) {
                // The refresh could not be completed (rate limited, outage,
                // offline) — that is not a dead session. Carry on with the
                // cached user; the next API call retries the refresh. Signing
                // out here is how a reload during the rate-limit window locked
                // a host out of the login page too.
                setCurrentUser(JSON.parse(storedUser));
              } else {
                clearAuthData();
              }
            } else {
              // Use stored user for now, will refresh on next API call
              setCurrentUser(JSON.parse(storedUser));
            }
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [getTokens, storeUser, clearAuthData]);

  // Refresh access token using the httpOnly refresh cookie (sent automatically
  // on this same-site request); no token is read from or sent by JS.
  // Returns true on success, false when the session is genuinely over, and
  // **null when it could not be determined** (rate limited, outage, offline).
  // Callers that sign the user out on a failure must treat null as "keep the
  // session" — see utils/refreshFailure for what that distinction cost.
  // null is falsy, so an `if (refreshed)` caller behaves exactly as before.
  const refreshToken = useCallback(async () => {
    try {
      const response = await authAPI.refresh();

      if (response.data.success) {
        storeTokens(response.data.accessToken);
        return true;
      }

      return false;
    } catch (err) {
      if (refreshMeansSessionEnded(err)) {
        return false;
      }
      // Transient. Say so plainly: the old line read "Token refresh failed",
      // which is what hosts reported as a token error while their session was
      // in fact fine.
      console.warn('Could not reach the session refresh right now; keeping the current session.');
      return null;
    }
  }, [storeTokens]);

  // Register new user
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      // Get referral data if user came from affiliate link
      const referralData = getReferralDataForConversion();
      const acquisition = getAcquisition();
      const registrationData = {
        ...userData,
        ...referralData,
        acquisition,
        adContext: getAdContext(acquisition),
      };

      const response = await authAPI.register(registrationData);

      if (response.data.success) {
        storeTokens(response.data.accessToken, response.data.refreshToken);
        storeUser(response.data.user);

        // Track signup for affiliate attribution
        if (referralData.affiliateCode) {
          trackSignupConversion(response.data.user.id).catch(err => {
            console.warn('Failed to track affiliate signup:', err);
          });
        }

        // PostHog: identify user and track signup
        identifyUser(response.data.user);
        trackEvent('user_signed_up', { plan: response.data.user.plan });
        // Meta: same event id as the server-side event, so it counts once.
        trackSiteConversion('CompleteRegistration', { status: 'registered' }, `signup_${response.data.user.id}`);

        return response.data;
      }

      throw new Error(response.data.message || 'Registration failed');
    } catch (err) {
      const message = toUserFacingError(err, 'Registration failed. Please try again.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login user.
  //
  // Three possible return shapes:
  //   1. { twoFactorRequired: true, twoFactorChallengeToken }
  //      — 2FA is enabled on the account. Caller navigates to /login/2fa.
  //      No tokens are stored; no user state is set.
  //   2. { success: true, user, accessToken, refreshToken }
  //      — Normal login. Tokens stored, user state set.
  //   3. Error thrown on failure.
  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.login(credentials);

      if (response.data.success && response.data.twoFactorRequired) {
        // Intentionally do NOT store tokens or user here — the account is
        // not fully authenticated until /2fa/challenge succeeds. The caller
        // is expected to route to the challenge page with this token.
        return {
          twoFactorRequired: true,
          twoFactorChallengeToken: response.data.twoFactorChallengeToken,
        };
      }

      if (response.data.success) {
        storeTokens(response.data.accessToken, response.data.refreshToken);
        storeUser(response.data.user);

        // PostHog: identify user on login
        identifyUser(response.data.user);
        trackEvent('user_logged_in', { plan: response.data.user.plan, role: response.data.user.role });

        return response.data;
      }

      throw new Error(response.data.message || 'Login failed');
    } catch (err) {
      const message = toUserFacingError(err, 'Login failed. Please try again.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Completes login after the user enters a TOTP / recovery code on the
  // challenge page. Returns the same shape as a successful normal login.
  const completeTwoFactorChallenge = async (twoFactorChallengeToken, code) => {
    try {
      setError(null);
      setLoading(true);

      const response = await authAPI.twoFactorChallenge(twoFactorChallengeToken, code);

      if (response.data.success) {
        storeTokens(response.data.accessToken, response.data.refreshToken);
        storeUser(response.data.user);

        identifyUser(response.data.user);
        trackEvent('user_logged_in', {
          plan: response.data.user.plan,
          role: response.data.user.role,
          two_factor: true,
        });

        return response.data;
      }

      throw new Error(response.data.message || 'Invalid code');
    } catch (err) {
      const message = toUserFacingError(err, 'Invalid code. Please try again.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Log out, and leave the page we were on.
   *
   * This used to clear state and stop there, relying on PrivateRoute to bounce
   * the now-anonymous user to /login. That works, but the protected URL stays
   * the current history entry — so Back returns to it, and the next person to
   * sign in on this browser can land on the previous user's page (a deck, a
   * webinar) rather than their own dashboard.
   *
   * `replace()`, not `href`: it drops that entry instead of stacking a new one,
   * so Back cannot walk back into the signed-out session. The full document
   * load is deliberate too — it leaves no component or context still holding
   * the previous user's data.
   *
   * Read as `options?.redirect !== false` rather than a destructured default
   * because callers wire this straight to a handler (`onClick={logout}` in
   * HomePremium), so the first argument is often a click event. An event has
   * no `redirect`, so it reads undefined and redirects — the safe direction.
   * Opting out has to be deliberate.
   */
  const logout = useCallback(async (options) => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      resetUser();
      clearAuthData();
      if (options?.redirect !== false && typeof window !== 'undefined' && window.location) {
        replaceWith('/');
      }
    }
  }, [clearAuthData]);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!currentUser) return false;

    // Admin roles have access to everything below them
    const roleHierarchy = {
      [USER_ROLES.SUPER_ADMIN]: 4,
      [USER_ROLES.ADMIN]: 3,
      [USER_ROLES.HOST]: 2,
      [USER_ROLES.ATTENDEE]: 1,
    };

    const userLevel = roleHierarchy[currentUser.role] || 0;
    const requiredLevel = roleHierarchy[role] || 0;

    return userLevel >= requiredLevel;
  }, [currentUser]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return hasRole(USER_ROLES.ADMIN);
  }, [hasRole]);

  // Check if user is host or higher
  const isHost = useCallback(() => {
    return hasRole(USER_ROLES.HOST);
  }, [hasRole]);

  // Check subscription/trial status
  //
  // Access model:
  //   - admin                            → full access (role bypass)
  //   - paid plan + active subscription  → full access
  //   - trial (active, within 14 days)   → full access (Professional tier)
  //   - free plan (post-trial)           → free-tier access
  //
  // The Free plan is a real product tier with its own limits defined in
  // backend/src/services/stripeService.js (1 webinar/month, 50 attendees,
  // 720p, Facebook only, etc.). Per-feature quantity and capability checks
  // live in backend/src/middleware/planLimits.js — that's where "free
  // users can create at most 1 webinar/month" is enforced, not here.
  //
  // Previously this function excluded `plan === 'free'` from hasAccess,
  // which meant that after the 14-day trial expired, users were routed
  // to <SubscriptionGate /> on every requireSubscription route and
  // couldn't access ANY features — even the ones the free tier is
  // supposed to provide. The trial-ended email explicitly promises
  // "Your account has been moved to the Free plan (1 webinar/month, 50
  // attendees, Facebook streaming)", which the code then contradicted.
  const getSubscriptionStatus = useCallback(() => {
    if (!currentUser) {
      return {
        hasAccess: false,
        isTrialing: false,
        trialExpired: false,
        hasPaidPlan: false,
        plan: null,
        daysLeftInTrial: 0,
      };
    }

    const hasPaidPlan = currentUser.plan && currentUser.plan !== 'none' && currentUser.plan !== 'free';
    const isActiveSubscription = currentUser.subscriptionStatus === 'active' && hasPaidPlan;
    // Free is the baseline tier — any authenticated user on the free plan
    // has access to free-tier features. Users with plan === 'none' (a
    // rare admin-assigned state) stay blocked.
    const isFreePlan = currentUser.plan === 'free';

    // The rule (and why it has two modes) lives in utils/planAccess.js.
    const { hasPlanAccess, lapsed } = planAccessFor(currentUser);

    return {
      hasPlanAccess,
      lapsed,
      planLapse: currentUser.planLapse || null,
      // isAdminRole covers super_admin too. This previously read
      // `role === 'admin'`, so a super_admin with plan 'none' and no active
      // subscription was denied access outright — the one role that should
      // never be gated.
      hasAccess: isAdminRole(currentUser.role) || isActiveSubscription || currentUser.isTrialing || isFreePlan,
      isTrialing: currentUser.isTrialing || false,
      trialExpired: currentUser.trialExpired || false,
      hasPaidPlan,
      isActiveSubscription,
      isFreePlan,
      plan: currentUser.plan,
      subscriptionStatus: currentUser.subscriptionStatus,
      daysLeftInTrial: currentUser.daysLeftInTrial || 0,
      trialEndsAt: currentUser.trialEndsAt,
    };
  }, [currentUser]);

  // Check if user has access (subscription or valid trial)
  const hasAccess = useCallback(() => {
    const status = getSubscriptionStatus();
    return status.hasAccess;
  }, [getSubscriptionStatus]);

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setError(null);

      const response = await authAPI.updateProfile(profileData);

      if (response.data.success) {
        storeUser(response.data.user);
        return response.data;
      }

      throw new Error(response.data.message || 'Profile update failed');
    } catch (err) {
      const message = toUserFacingError(err, 'Profile update failed. Please try again.');
      setError(message);
      throw err;
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);

      const response = await authAPI.changePassword(currentPassword, newPassword);

      if (response.data.success) {
        return response.data;
      }

      throw new Error(response.data.message || 'Password change failed');
    } catch (err) {
      const message = toUserFacingError(err, 'Password change failed. Please try again.');
      setError(message);
      throw err;
    }
  };

  // Re-fetch user profile (e.g. after creating first webinar, to update activityCounts)
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.data.success) {
        storeUser(response.data.user);
      }
    } catch {
      // Silent fail — data refreshes on next full load
    }
  }, [storeUser]);

  // Initiate Google OAuth login
  const initiateGoogleLogin = (returnUrl = '/dashboard', mode = 'login') => {
    const backendUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
    const googleAuthUrl = `${backendUrl}/auth/google?returnUrl=${encodeURIComponent(returnUrl)}&mode=${mode}`;
    goTo(googleAuthUrl);
  };

  // Handle Google OAuth callback tokens.
  // useCallback is load-bearing: AuthCallback lists this in its effect deps,
  // and the effect itself calls in here (setLoading → storeUser → setLoading).
  // As a plain function it got a new identity on every provider render, so the
  // effect re-ran on the renders it had just caused — a self-feeding loop that
  // re-verified the session over and over, each turn replacing `currentUser`
  // and refetching anything keyed on it.
  const handleGoogleCallback = useCallback(async (accessToken, refreshToken) => {
    try {
      setError(null);
      setLoading(true);

      // Store tokens
      storeTokens(accessToken, refreshToken);

      // Get user profile
      const response = await authAPI.getProfile();

      if (response.data.success) {
        storeUser(response.data.user);
        return response.data;
      }

      throw new Error('Failed to get user profile');
    } catch (err) {
      const message = toUserFacingError(err, 'Google login failed. Please try again.');
      setError(message);
      clearAuthData();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [storeTokens, storeUser, clearAuthData]);

  // Verify session using httpOnly cookies (no tokens needed in JS)
  // Used after Google OAuth redirect where cookies are set by backend
  const verifySession = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Call /auth/me — httpOnly cookies are sent automatically via withCredentials
      const response = await authAPI.getProfile();

      if (response.data.success) {
        storeUser(response.data.user);
        return response.data;
      }

      throw new Error('Failed to verify session');
    } catch (err) {
      const message = toUserFacingError(err, 'Session verification failed. Please try again.');
      setError(message);
      clearAuthData();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [storeUser, clearAuthData]);

  // Login/signup with Google token (for frontend-initiated OAuth)
  const googleLogin = async (googleAccessToken) => {
    try {
      setError(null);
      setLoading(true);

      const acquisition = getAcquisition();
      const response = await api.post('/auth/google/token', {
        accessToken: googleAccessToken,
        acquisition,
        adContext: getAdContext(acquisition),
      });

      if (response.data.success) {
        storeTokens(response.data.accessToken, response.data.refreshToken);
        storeUser(response.data.user);

        // Track signup for affiliate attribution if new user
        if (response.data.isNewUser) {
          trackSiteConversion('CompleteRegistration', { status: 'registered' }, `signup_${response.data.user.id}`);
          const referralData = getReferralDataForConversion();
          if (referralData.affiliateCode) {
            trackSignupConversion(response.data.user.id).catch(err => {
              console.warn('Failed to track affiliate signup:', err);
            });
          }
        }

        return response.data;
      }

      throw new Error(response.data.message || 'Google login failed');
    } catch (err) {
      const message = toUserFacingError(err, 'Google login failed. Please try again.');
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    isAuthenticated: !!currentUser,
    register,
    login,
    completeTwoFactorChallenge,
    logout,
    refreshToken,
    hasRole,
    isAdmin,
    isHost,
    hasAccess,
    getSubscriptionStatus,
    updateProfile,
    changePassword,
    refreshUser,
    getTokens,
    initiateGoogleLogin,
    handleGoogleCallback,
    // Same thing under a neutral name: store tokens the server just issued,
    // then load the profile (used by the founding checkout return page).
    signInWithTokens: handleGoogleCallback,
    verifySession,
    googleLogin,
    roles: USER_ROLES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
